import { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { cn } from '@/utils/cn'
import { useSearchStore } from '@/stores/searchStore'
import { useTranslation } from '@/stores/i18nStore'
import { SearchIcon } from '@/components/ui/icons'
import type { MatchType, ProgressiveResults as ProgressiveResultsType, TranslationKey } from '@/types'

// Match types for displaying results
const MATCH_TYPE_KEYS: Record<string, TranslationKey> = {
  exact: 'exact',
  prefix: 'prefix',
  suffix: 'suffix',
  contains: 'contains',
  sentences: 'sentences',
  all: 'all',
  digits: 'digits',
  written: 'written'
}

// Batch size for infinite scroll
const BATCH_SIZE = 50

export function SearchResults() {
  const { t } = useTranslation()
  const {
    searchResults,
    progressiveResults,
    isProgressiveMode,
    isLoading,
    searchString
  } = useSearchStore()

  const [activeTab, setActiveTab] = useState<string>('overview')

  // Collect all matched words for sentence highlighting - must be before early returns
  // This includes all word matches from exact, prefix, suffix, contains, digits, written
  const allMatchedWords = useMemo(() => {
    if (!searchResults?.results) return []
    const words: string[] = []
    const wordTypes = ['exact', 'prefix', 'suffix', 'contains', 'digits', 'written', 'all']
    for (const type of wordTypes) {
      const matches = searchResults.results[type as keyof typeof searchResults.results]
      if (Array.isArray(matches) && matches.length > 0) {
        words.push(...matches)
      }
    }
    return [...new Set(words)] // Remove duplicates
  }, [searchResults])

  if (isLoading && !isProgressiveMode) {
    return <SearchResultsLoading />
  }

  if (!searchResults && !isProgressiveMode) {
    return null
  }

  // Organize results by match type
  const organizedResults: Record<string, string[]> = {
    exact: [],
    prefix: [],
    suffix: [],
    contains: [],
    sentences: [],
    all: [],
    digits: [],
    written: []
  }

  let totalMatches = 0
  let isNumbersMode = false

  if (searchResults?.results) {
    // Check if this is numbers mode (has digits or written keys)
    isNumbersMode = 'digits' in searchResults.results || 'written' in searchResults.results

    for (const [matchType, matches] of Object.entries(searchResults.results)) {
      if (Array.isArray(matches) && matches.length > 0) {
        const uniqueMatches = [...new Set(matches)]
        organizedResults[matchType] = uniqueMatches
        // For numbers mode, only count 'all' to avoid double counting digits+written
        if (isNumbersMode) {
          if (matchType === 'all') {
            totalMatches += uniqueMatches.length
          }
        } else {
          totalMatches += uniqueMatches.length
        }
      }
    }
  }

  if (searchResults?.sentences && searchResults.sentences.length > 0) {
    organizedResults.sentences = searchResults.sentences
    totalMatches += searchResults.sentences.length
  }

  const availableMatchTypes = Object.keys(organizedResults).filter(
    (mt) => organizedResults[mt].length > 0
  )

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="max-w-5xl mx-auto mt-8"
    >
      {/* Progressive Status */}
      {isProgressiveMode && (
        <ProgressiveStatus progressiveResults={progressiveResults} />
      )}

      {/* Results Summary */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-6 pb-4 border-b border-zinc-200 dark:border-zinc-800"
      >
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h2 className="text-lg font-medium text-zinc-900 dark:text-white">
              {t('resultsFor')} "<span className="text-zinc-600 dark:text-zinc-400 font-ethiopic">{searchString}</span>"
            </h2>
            <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-1">
              {totalMatches} {totalMatches === 1 ? t('matchSingular') : t('matches')} in {availableMatchTypes.length} {availableMatchTypes.length === 1 ? t('categorySingular') : t('categories')}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {availableMatchTypes.map((matchType) => {
              const count = organizedResults[matchType].length
              return (
                <span
                  key={matchType}
                  className="px-2 py-1 text-xs font-medium text-zinc-500 dark:text-zinc-400 bg-zinc-100 dark:bg-zinc-800 rounded"
                >
                  {t(MATCH_TYPE_KEYS[matchType])}: {count}
                </span>
              )
            })}
          </div>
        </div>
      </motion.div>

      {/* Match Type Tabs */}
      <div className="flex gap-1 mb-6 overflow-x-auto pb-2">
        <TabButton
          active={activeTab === 'overview'}
          onClick={() => setActiveTab('overview')}
          label={t('all')}
          count={totalMatches}
        />
        {availableMatchTypes.map((matchType) => {
          const count = organizedResults[matchType].length
          return (
            <TabButton
              key={matchType}
              active={activeTab === matchType}
              onClick={() => setActiveTab(matchType)}
              label={t(MATCH_TYPE_KEYS[matchType])}
              count={count}
            />
          )
        })}
      </div>

      {/* Results Content */}
      <AnimatePresence mode="wait">
        <motion.div
          key={activeTab}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="space-y-6"
        >
          {activeTab === 'overview' ? (
            availableMatchTypes.length > 0 ? (
              availableMatchTypes.map((matchType) => (
                <ResultSection
                  key={matchType}
                  matchType={matchType}
                  matches={organizedResults[matchType]}
                  searchString={searchString}
                  highlightTerms={matchType === 'sentences' ? allMatchedWords : undefined}
                  onViewMore={() => setActiveTab(matchType)}
                  isNumbersMode={isNumbersMode}
                />
              ))
            ) : (
              <NoResults />
            )
          ) : organizedResults[activeTab]?.length > 0 ? (
            <ResultSection
              key={activeTab}
              matchType={activeTab}
              matches={organizedResults[activeTab]}
              searchString={searchString}
              highlightTerms={activeTab === 'sentences' ? allMatchedWords : undefined}
              fullView
              isNumbersMode={isNumbersMode}
            />
          ) : (
            <NoResults matchType={activeTab} />
          )}
        </motion.div>
      </AnimatePresence>
    </motion.div>
  )
}

interface TabButtonProps {
  active: boolean
  onClick: () => void
  label: string
  count: number
}

function TabButton({ active, onClick, label, count }: TabButtonProps) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors',
        active
          ? 'bg-zinc-900 dark:bg-white text-white dark:text-zinc-900'
          : 'text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white hover:bg-zinc-100 dark:hover:bg-zinc-800'
      )}
    >
      <span>{label}</span>
      <span className={cn(
        'text-xs',
        active ? 'text-zinc-400 dark:text-zinc-500' : 'text-zinc-400'
      )}>
        {count}
      </span>
    </button>
  )
}

interface ResultSectionProps {
  matchType: string
  matches: string[]
  searchString: string
  highlightTerms?: string[]  // For highlighting multiple terms (e.g., numbers)
  fullView?: boolean
  onViewMore?: () => void
  isNumbersMode?: boolean  // Whether to highlight digit patterns
}

function ResultSection({
  matchType,
  matches,
  searchString,
  highlightTerms,
  fullView = false,
  onViewMore,
  isNumbersMode = false
}: ResultSectionProps) {
  const { t } = useTranslation()
  const isSentences = matchType === 'sentences'

  // For overview, show limited items; for full view, use infinite scroll
  const initialCount = fullView ? BATCH_SIZE : (isSentences ? 10 : 50)
  const [displayCount, setDisplayCount] = useState(initialCount)
  const loadMoreRef = useRef<HTMLDivElement>(null)

  // Reset display count when matches change
  useEffect(() => {
    setDisplayCount(initialCount)
  }, [matches, initialCount])

  // Infinite scroll observer
  useEffect(() => {
    if (!fullView) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && displayCount < matches.length) {
          setDisplayCount(prev => Math.min(prev + BATCH_SIZE, matches.length))
        }
      },
      { threshold: 0.1 }
    )

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current)
    }

    return () => observer.disconnect()
  }, [fullView, displayCount, matches.length])

  const displayedMatches = matches.slice(0, displayCount)
  // Only show "load more" indicator in fullView mode where infinite scroll is active
  const hasMore = fullView && displayCount < matches.length
  // For overview mode, show "...X more" link
  const remainingCount = matches.length - displayCount
  const showViewMoreLink = !fullView && remainingCount > 0 && onViewMore

  return (
    <div className="border border-zinc-200 dark:border-zinc-800 rounded-lg overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 bg-zinc-50 dark:bg-zinc-900/50 border-b border-zinc-200 dark:border-zinc-800">
        <div className="flex items-center justify-between">
          <span className="font-medium text-zinc-900 dark:text-white text-sm">
            {t(MATCH_TYPE_KEYS[matchType])}
          </span>
          <span className="text-xs text-zinc-500 dark:text-zinc-400">
            {matches.length} {matches.length === 1 ? t('matchSingular') : t('matches')}
          </span>
        </div>
      </div>

      {/* Content */}
      {isSentences ? (
        <SentenceGrid
          sentences={displayedMatches}
          searchString={searchString}
          highlightTerms={highlightTerms}
          hasMore={hasMore}
          loadMoreRef={loadMoreRef}
          isNumbersMode={isNumbersMode}
        />
      ) : (
        <WordGrid
          words={displayedMatches}
          hasMore={hasMore}
          loadMoreRef={loadMoreRef}
        />
      )}

      {/* View more link for overview mode */}
      {showViewMoreLink && (
        <button
          onClick={onViewMore}
          className="w-full py-3 text-sm text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors border-t border-zinc-200 dark:border-zinc-800"
        >
          ...{remainingCount} {t('more')} →
        </button>
      )}
    </div>
  )
}

interface WordGridProps {
  words: string[]
  hasMore: boolean
  loadMoreRef: React.RefObject<HTMLDivElement>
}

function WordGrid({ words, hasMore, loadMoreRef }: WordGridProps) {
  return (
    <div className="overflow-x-auto">
      {/* Excel-like grid for words */}
      <div
        className="grid gap-px bg-zinc-200 dark:bg-zinc-700"
        style={{
          gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))'
        }}
      >
        {words.map((word, i) => (
          <div
            key={i}
            className={cn(
              'px-3 py-2 bg-white dark:bg-zinc-900',
              'text-sm font-ethiopic text-zinc-700 dark:text-zinc-300',
              'truncate text-center',
              'hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors'
            )}
            title={word}
          >
            {word}
          </div>
        ))}
      </div>

      {/* Load more trigger */}
      {hasMore && (
        <div
          ref={loadMoreRef}
          className="py-4 flex items-center justify-center bg-zinc-50 dark:bg-zinc-900/50 border-t border-zinc-200 dark:border-zinc-800"
        >
          <div className="flex items-center gap-2 text-sm text-zinc-500 dark:text-zinc-400">
            <div className="w-4 h-4 border-2 border-zinc-300 dark:border-zinc-600 border-t-zinc-500 dark:border-t-zinc-400 rounded-full animate-spin" />
            <span>Loading more...</span>
          </div>
        </div>
      )}
    </div>
  )
}

interface SentenceGridProps {
  sentences: string[]
  searchString: string
  highlightTerms?: string[]  // For highlighting multiple terms (e.g., numbers)
  hasMore: boolean
  loadMoreRef: React.RefObject<HTMLDivElement>
  isNumbersMode?: boolean  // Whether to highlight digit patterns
}

function SentenceGrid({ sentences, searchString, highlightTerms, hasMore, loadMoreRef, isNumbersMode = false }: SentenceGridProps) {
  // Create a Set for O(1) lookup when we have many highlight terms
  // Use Unicode NFC normalization to ensure consistent character representation
  const highlightSet = useMemo(() => {
    if (highlightTerms && highlightTerms.length > 0) {
      return new Set(highlightTerms.map(t => t.normalize('NFC')))
    }
    return null
  }, [highlightTerms])

  const highlightMatch = useCallback((text: string) => {
    // Normalize the text for consistent matching
    const normalizedText = text.normalize('NFC')

    // Highlight matched words from the highlightSet
    if (highlightSet && highlightSet.size > 0) {
      const result: (string | JSX.Element)[] = []
      let lastIndex = 0
      const matches: Array<{start: number, end: number, text: string}> = []
      let match

      // Only highlight digits in numbers search mode
      if (isNumbersMode) {
        const digitPattern = /[0-9፩-፱፲-፼]+/g
        while ((match = digitPattern.exec(normalizedText)) !== null) {
          matches.push({ start: match.index, end: match.index + match[0].length, text: match[0] })
        }
      }

      // Find Amharic words by checking each word against the set
      // Ethiopic syllables only (U+1200-U+135A), excluding punctuation (U+1360-U+1368) and digits (U+1369-U+137C)
      // Also include Ethiopic Supplement (U+1380-U+1399) and Extended (U+2D80-U+2DDF)
      const wordPattern = /[\u1200-\u135A\u1380-\u1399\u2D80-\u2DDF]+/g
      while ((match = wordPattern.exec(normalizedText)) !== null) {
        // Use NFC normalized comparison (Amharic has no case, so no toLowerCase needed)
        if (highlightSet.has(match[0])) {
          // Check if this doesn't overlap with existing matches
          const overlaps = matches.some(m =>
            (match!.index >= m.start && match!.index < m.end) ||
            (match!.index + match![0].length > m.start && match!.index + match![0].length <= m.end)
          )
          if (!overlaps) {
            matches.push({ start: match.index, end: match.index + match[0].length, text: match[0] })
          }
        }
      }

      // Sort matches by position
      matches.sort((a, b) => a.start - b.start)

      // Build result with highlighted portions
      for (const m of matches) {
        if (m.start > lastIndex) {
          result.push(normalizedText.slice(lastIndex, m.start))
        }
        result.push(
          <mark key={m.start} className="bg-yellow-200 dark:bg-yellow-900/50 px-0.5 rounded">
            {m.text}
          </mark>
        )
        lastIndex = m.end
      }

      // Add remaining text
      if (lastIndex < normalizedText.length) {
        result.push(normalizedText.slice(lastIndex))
      }

      return result.length > 0 ? result : normalizedText
    }

    // For regular search string highlighting
    if (!searchString) return normalizedText

    try {
      // Normalize search string too for consistent matching
      const normalizedSearch = searchString.normalize('NFC')
      const escaped = normalizedSearch.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      const regex = new RegExp(`(${escaped})`, 'gi')
      const parts = normalizedText.split(regex)
      return parts.map((part, i) => {
        // Check if this part matches (NFC normalized comparison)
        if (part.normalize('NFC') === normalizedSearch) {
          return (
            <mark key={i} className="bg-yellow-200 dark:bg-yellow-900/50 px-0.5 rounded">
              {part}
            </mark>
          )
        }
        return part
      })
    } catch {
      return normalizedText
    }
  }, [searchString, highlightSet, isNumbersMode])

  return (
    <div className="overflow-x-auto">
      {/* Excel-like single-column grid for sentences */}
      <div className="divide-y divide-zinc-200 dark:divide-zinc-700">
        {sentences.map((sentence, i) => (
          <div
            key={i}
            className={cn(
              'px-4 py-3',
              'bg-white dark:bg-zinc-900',
              'hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors',
              i % 2 === 1 && 'bg-zinc-50/50 dark:bg-zinc-900/50'
            )}
          >
            <div className="flex items-start gap-3">
              {/* Row number */}
              <span className="flex-shrink-0 w-8 text-xs text-zinc-400 dark:text-zinc-500 font-mono pt-0.5">
                {i + 1}
              </span>
              {/* Sentence content */}
              <p className="font-ethiopic text-sm text-zinc-700 dark:text-zinc-300 leading-relaxed flex-1">
                {highlightMatch(sentence)}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Load more trigger */}
      {hasMore && (
        <div
          ref={loadMoreRef}
          className="py-4 flex items-center justify-center bg-zinc-50 dark:bg-zinc-900/50 border-t border-zinc-200 dark:border-zinc-800"
        >
          <div className="flex items-center gap-2 text-sm text-zinc-500 dark:text-zinc-400">
            <div className="w-4 h-4 border-2 border-zinc-300 dark:border-zinc-600 border-t-zinc-500 dark:border-t-zinc-400 rounded-full animate-spin" />
            <span>Loading more...</span>
          </div>
        </div>
      )}
    </div>
  )
}

interface ProgressiveStatusProps {
  progressiveResults: ProgressiveResultsType
}

function ProgressiveStatus({ progressiveResults }: ProgressiveStatusProps) {
  const { t } = useTranslation()
  const phases: MatchType[] = ['exact', 'prefix', 'suffix', 'contains', 'sentences']
  const completedCount = Object.keys(progressiveResults).length

  return (
    <div className="mb-6 p-4 border border-zinc-200 dark:border-zinc-800 rounded-lg bg-zinc-50 dark:bg-zinc-900/50">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-4 h-4 border-2 border-zinc-400 border-t-transparent rounded-full animate-spin" />
        <span className="text-sm font-medium text-zinc-700 dark:text-zinc-300">
          {t('searching')} {completedCount} / {phases.length}
        </span>
      </div>
      <div className="flex gap-2">
        {phases.map((phase) => {
          const isCompleted = !!progressiveResults[phase]
          return (
            <span
              key={phase}
              className={cn(
                'px-2 py-1 text-xs rounded',
                isCompleted
                  ? 'bg-zinc-900 dark:bg-white text-white dark:text-zinc-900'
                  : 'bg-zinc-200 dark:bg-zinc-800 text-zinc-400'
              )}
            >
              {t(MATCH_TYPE_KEYS[phase])}
            </span>
          )
        })}
      </div>
    </div>
  )
}

function NoResults({ matchType }: { matchType?: string }) {
  const { t } = useTranslation()
  return (
    <div className="text-center py-12">
      <div className="w-12 h-12 mx-auto mb-4 rounded-lg bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center">
        <SearchIcon className="w-6 h-6 text-zinc-400" />
      </div>
      <h3 className="font-medium text-zinc-700 dark:text-zinc-300 mb-1">
        {matchType ? `${t(MATCH_TYPE_KEYS[matchType])}: ${t('noMatchesFound').toLowerCase()}` : t('noMatchesFound')}
      </h3>
      <p className="text-sm text-zinc-500 dark:text-zinc-400">
        {t('tryAdjustingSearch')}
      </p>
    </div>
  )
}

function SearchResultsLoading() {
  return (
    <div className="max-w-5xl mx-auto mt-8">
      {/* Summary skeleton */}
      <div className="mb-6 pb-4 border-b border-zinc-200 dark:border-zinc-800">
        <div className="h-5 w-48 bg-zinc-200 dark:bg-zinc-800 rounded animate-pulse mb-2" />
        <div className="h-4 w-32 bg-zinc-200 dark:bg-zinc-800 rounded animate-pulse" />
      </div>

      {/* Tabs skeleton */}
      <div className="flex gap-2 mb-6">
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className="h-8 w-20 rounded-md bg-zinc-200 dark:bg-zinc-800 animate-pulse"
          />
        ))}
      </div>

      {/* Grid skeleton */}
      <div className="border border-zinc-200 dark:border-zinc-800 rounded-lg overflow-hidden">
        <div className="h-12 bg-zinc-100 dark:bg-zinc-900 animate-pulse" />
        <div
          className="grid gap-px bg-zinc-200 dark:bg-zinc-700 p-px"
          style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))' }}
        >
          {Array.from({ length: 20 }).map((_, i) => (
            <div
              key={i}
              className="h-10 bg-white dark:bg-zinc-900 animate-pulse"
            />
          ))}
        </div>
      </div>
    </div>
  )
}
